import { signInAnonymously } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { doc, onSnapshot, runTransaction, serverTimestamp, updateDoc } from "firebase/firestore";
import { authWeb, dbWeb, functionsWeb } from "../../firebaseConfig";
import {
  normalizeAnnouncement,
  normalizeEtvPreview,
  validateAnnouncement,
  validateDemoKuliah,
  validateLeadInput,
} from "./demoContract";

const safeError = (error) => {
  const mapped = new Error("Demo belum dapat dimulakan. Sila cuba sekali lagi.");
  mapped.userMessage = error?.code === "functions/invalid-argument"
    ? "Semak semula maklumat yang dimasukkan."
    : error?.code === "functions/resource-exhausted"
      ? "Terlalu banyak percubaan. Sila cuba lagi dalam 10 minit."
      : "Demo belum dapat dimulakan. Sila cuba sekali lagi.";
  mapped.code = error?.code || "demo/unknown";
  return mapped;
};

const randomUUID = () => {
  if (typeof crypto?.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
    .slice(6, 8)
    .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
};

export const DEMO_CALLABLE_OPTIONS = Object.freeze({
  limitedUseAppCheckTokens: true,
});

const defaultAdapter = {
  randomUUID,
  getCurrentUser: () => authWeb.currentUser,
  signInAnonymously: () => signInAnonymously(authWeb),
  callCreateDemoSession: (payload) =>
    httpsCallable(functionsWeb, "createDemoSession", DEMO_CALLABLE_OPTIONS)(payload),
  makeSessionRef: (sessionId) => doc(dbWeb, "demoSessions", sessionId),
  subscribeToRef: (ref, next, error) =>
    onSnapshot(
      ref,
      (snapshot) => next(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null),
      error
    ),
  updateRef: (ref, payload) => updateDoc(ref, payload),
  runSessionTransaction: (ref, work) =>
    runTransaction(dbWeb, async (transaction) => {
      const snapshot = await transaction.get(ref);
      if (!snapshot.exists()) throw new Error("demo/session-not-found");
      return work({
        data: () => snapshot.data(),
        update: (payload) => transaction.update(ref, payload),
      });
    }),
  serverTimestamp,
};

export const createDemoService = (adapter = defaultAdapter) => {
  let pendingRequestId = "";
  let createInFlight = null;

  const createQualifiedDemo = (lead) => {
    if (createInFlight) return createInFlight;

    const validation = validateLeadInput(lead);
    if (!validation.valid) {
      const error = new Error("Maklumat demo tidak lengkap.");
      error.userMessage = "Semak semula maklumat yang dimasukkan.";
      error.validation = validation;
      return Promise.reject(error);
    }

    if (!pendingRequestId) pendingRequestId = adapter.randomUUID();
    const requestId = pendingRequestId;

    createInFlight = (async () => {
      try {
        if (!adapter.getCurrentUser()) await adapter.signInAnonymously();
        const response = await adapter.callCreateDemoSession({
          ...validation.value,
          requestId,
        });
        pendingRequestId = "";
        return response.data;
      } catch (error) {
        throw safeError(error);
      } finally {
        createInFlight = null;
      }
    })();

    return createInFlight;
  };

  const subscribeDemoSession = (sessionId, callbacks = {}) => {
    const ref = adapter.makeSessionRef(sessionId);
    return adapter.subscribeToRef(ref, callbacks.next || (() => {}), callbacks.error);
  };

  const mutateContent = (sessionId, contentType, makePayload) => {
    const ref = adapter.makeSessionRef(sessionId);
    return adapter.runSessionTransaction(ref, ({ data, update }) => {
      const current = data() || {};
      const updatedAt = adapter.serverTimestamp();
      if (current.schemaVersion !== 2) {
        if (contentType !== "announcement") return null;
        return update({ ...makePayload({ current, updatedAt, nextRevision: 0 }), updatedAt });
      }
      const nextRevision = (Number.isSafeInteger(current.delivery?.revision)
        ? current.delivery.revision
        : 0) + 1;
      return update({
        ...makePayload({ current, updatedAt, nextRevision }),
        delivery: {
          contentType,
          revision: nextRevision,
          etv: "idle",
          app: "idle",
        },
        updatedAt,
      });
    });
  };

  const sendDemoAnnouncement = async ({ sessionId, announcement }) => {
    const validation = validateAnnouncement(announcement);
    if (!validation.valid) {
      const error = new Error("Makluman demo tidak lengkap.");
      error.userMessage = "Lengkapkan tajuk dan mesej pengumuman.";
      error.validation = validation;
      throw error;
    }
    return mutateContent(sessionId, "announcement", ({ updatedAt }) => ({
      status: "sent",
      activeStep: "delivered",
      announcement: { ...normalizeAnnouncement(validation.value), sentAt: updatedAt },
    }));
  };

  const resetDemoSession = (sessionId) =>
    mutateContent(sessionId, "announcement", () => ({
      status: "draft",
      activeStep: "compose",
      announcement: { title: "", message: "", sentAt: null },
    }));

  const publishDemoKuliah = async ({ sessionId, kuliah }) => {
    const validation = validateDemoKuliah(kuliah);
    if (!validation.valid) {
      const error = new Error("Maklumat kuliah demo tidak lengkap.");
      error.userMessage = validation.errors.dateTime
        || "Lengkapkan maklumat kuliah terlebih dahulu.";
      error.validation = validation;
      throw error;
    }
    return mutateContent(sessionId, "kuliah", ({ updatedAt }) => ({
      status: "sent",
      activeStep: "delivered",
      kuliah: { ...validation.value, publishedAt: updatedAt },
    }));
  };

  const clearDemoKuliah = (sessionId) =>
    mutateContent(sessionId, "kuliah", () => ({ kuliah: null }));

  const setDemoEtvPreview = ({ sessionId, mode, prayer = "Maghrib" }) => {
    const preview = normalizeEtvPreview({ mode, prayer });
    return mutateContent(sessionId, "etv_mode", ({ updatedAt, nextRevision }) => ({
      etvPreview: {
        ...preview,
        sequence: nextRevision,
        startedAt: preview.mode === "normal" ? null : updatedAt,
      },
    }));
  };

  const markDemoRendered = ({ sessionId, target, revision, contentType } = {}) => {
    if (!sessionId || !["etv", "app"].includes(target)) return Promise.resolve();
    const ref = adapter.makeSessionRef(sessionId);
    return adapter.runSessionTransaction(ref, ({ data, update }) => {
      const current = data() || {};
      if (
        current.schemaVersion !== 2
        || current.delivery?.revision !== revision
        || current.delivery?.contentType !== contentType
      ) {
        return null;
      }
      return update({
        delivery: { ...current.delivery, [target]: "received" },
        updatedAt: adapter.serverTimestamp(),
      });
    });
  };

  return {
    clearDemoKuliah,
    createQualifiedDemo,
    markDemoRendered,
    publishDemoKuliah,
    resetDemoSession,
    sendDemoAnnouncement,
    setDemoEtvPreview,
    subscribeDemoSession,
  };
};

const demoService = createDemoService();

export const createQualifiedDemo = demoService.createQualifiedDemo;
export const clearDemoKuliah = demoService.clearDemoKuliah;
export const markDemoRendered = demoService.markDemoRendered;
export const publishDemoKuliah = demoService.publishDemoKuliah;
export const resetDemoSession = demoService.resetDemoSession;
export const sendDemoAnnouncement = demoService.sendDemoAnnouncement;
export const setDemoEtvPreview = demoService.setDemoEtvPreview;
export const subscribeDemoSession = demoService.subscribeDemoSession;
